<template>
  <main class="flex-1 relative overflow-y-auto focus:outline-none" tabindex="0">
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="md:flex md:items-center md:justify-between">
        <div class="flex min-w-0">
          <svg-vue class="content-title-icon" icon="font-awesome.users-solid"></svg-vue>
          <h1 class="content-title">
            {{ $t('Users') }}
          </h1>
        </div>
        <div class="mt-4 flex md:mt-0 md:ml-4">
          <router-link class="btn btn-app rounded-none rounded-l-md" to="/admin/users/new">{{ $t('Create') }}</router-link>
          <router-link class="btn btn-app rounded-none rounded-r-md" to="/admin">
            {{ $t('Back') }}
          </router-link>
        </div>
      </div>
    </div>
    <div class="my-6 max-w-1/6 mx-auto px-4 sm:px-6 lg:px-8">
      <div class="content-card-body">
        <loading :status="loading"></loading>
        <div class="p-4 md:w-2/3 items-center md:flex md:justify-between sm:px-6">
          <!-- component -->
          <input type="text" id="search" v-model.lazy="search" :placeholder="$t('Search')" @change="getUsers" class="form-input rounded-none rounded-l-md" />
          <div class="flex relative w-full">
            <input-select
              :options="sortOptions"
              @change="changeSort"
              class="w-full"
              classes="form-input rounded-none"
              id="sorting"
              required
              option-label="value"
              v-model="sort.column"
            ></input-select>
          </div>
          <button @click="changeSort" class="flex text-gray-800 rounded-none rounded-r-md font-bold py-2 border border-gray-300 px-6" type="button">
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-down-alt-regular" v-show="sort.order === 'asc'"></svg-vue>
            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.sort-amount-up-alt-regular" v-show="sort.order === 'desc'"></svg-vue>
            <span class="ml-2">{{ $t('Sort') }}</span>
          </button>
        </div>
        <div class="border-t border-gray-200">
          <template v-if="userList.length > 0">
            <ul>
              <template v-for="(user, index) in userList">
                <li :class="{ 'border-t border-gray-200': index !== 0 }" :key="index">
                  <router-link
                    :to="user.id !== $store.getters['app/getUserId'] ? `/admin/users/${user.id}/edit` : '/account'"
                    class="block hover:bg-gray-100 focus:outline-none focus:bg-blue-100 transition duration-150 ease-in-out"
                  >
                    <div class="flex items-center px-4 py-4 sm:px-6">
                      <div class="min-w-0 flex-1 flex items-center">
                        <div class="flex-shrink-0">
                          <img :alt="$t('Avatar')" :src="user.avatar !== 'gravatar' ? user.avatar : user.gravatar" class="h-12 w-12 rounded-full" />
                        </div>
                        <div class="min-w-0 flex-1 px-4 lg:grid lg:grid-cols-2 lg:gap-4">
                          <div>
                            <div class="text-sm leading-5 font-medium text-blue-700 truncate">
                              {{ user.name }}
                            </div>
                            <div class="mt-2 flex items-center text-sm leading-5 text-gray-800">
                              <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-800" icon="font-awesome.envelope-solid"></svg-vue>
                              <span class="truncate">{{ user.email }}</span>
                            </div>
                          </div>
                          <div class="hidden lg:block">
                            <div class="text-sm leading-5 text-gray-900">
                              {{ $t('The user has the role') }}:
                              <strong>{{ user.role.name }}</strong>
                            </div>
                            <div class="mt-2 flex items-center text-sm leading-5 text-gray-900" v-show="user.status">
                              <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-green-400" icon="font-awesome.check-circle-solid"></svg-vue>
                              {{ $t('User is activated') }}
                            </div>
                            <div class="mt-2 flex items-center text-sm leading-5 text-red-500" v-show="!user.status">
                              <svg-vue class="flex-shrink-0 mr-1.5 h-4 w-4 text-red-400" icon="font-awesome.times-circle-solid"></svg-vue>
                              {{ $t('User is disabled') }}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div>
                        <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.angle-right-regular"></svg-vue>
                      </div>
                    </div>
                  </router-link>
                </li>
              </template>
            </ul>
          </template>
          <template v-else>
            <no-content class="my-10" :search="search" />
          </template>
        </div>
        <nav class="bg-white p-4 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div class="hidden sm:block">
            <p class="text-sm leading-5 text-gray-700">
              {{ $t('Showing') }}
              <span class="font-medium">{{ pagination.perPage * pagination.currentPage - pagination.perPage + 1 }}</span>
              {{ $t('to') }}
              <span class="font-medium">{{
                pagination.perPage * pagination.currentPage <= pagination.total ? pagination.perPage * pagination.currentPage : pagination.total
              }}</span>
              {{ $t('of') }}
              <span class="font-medium">{{ pagination.total }}</span>
              {{ $t('results') }}
            </p>
          </div>
          <div class="flex-1 flex justify-between sm:justify-end">
            <button
              :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
              :disabled="pagination.currentPage <= 1"
              @click="changePage(pagination.currentPage - 1)"
              class="pagination-link"
              type="button"
            >
              {{ $t('Previous') }}
            </button>
            <button
              :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
              :disabled="pagination.currentPage >= pagination.totalPages"
              @click="changePage(pagination.currentPage + 1)"
              class="ml-3 pagination-link"
              type="button"
            >
              {{ $t('Next') }}
            </button>
          </div>
        </nav>
      </div>
    </div>
  </main>
</template>
<script>
export default {
  name: 'list-users',
  metaInfo() {
    return {
      title: this.$i18n.t('Users'),
    };
  },
  data() {
    return {
      page: 1,
      search: '',
      sort: {
        order: 'asc',
        column: 'created_at',
      },
      sortOptions: [
        { id: 'name', value: this.$i18n.t('Name') },
        { id: 'email', value: this.$i18n.t('Email') },
        { id: 'created_at', value: this.$i18n.t('Created at') },
        { id: 'updated_at', value: this.$i18n.t('Updated at') },
      ],
      perPage: 10,
      loading: true,
      pagination: {
        currentPage: 0,
        perPage: 0,
        total: 0,
        totalPages: 0,
      },
      userList: [],
    };
  },
  mounted() {
    this.getUsers();
  },
  methods: {
    changePage(page) {
      if (page > 0 && page <= this.pagination.totalPages && page !== this.page) {
        this.page = page;
        this.getUsers();
      }
    },
    changeSort() {
      if (this.sort.order === 'asc') {
        this.sort.order = 'desc';
      } else if (this.sort.order === 'desc') {
        this.sort.order = 'asc';
      }
      this.getUsers();
    },
    getUsers() {
      this.loading = true;
      axios
        .get(route('users.index'), {
          params: {
            page: this.page,
            sort: this.sort,
            search: this.search,
            perPage: this.perPage,
          },
        })
        .then((response) => {
          this.userList = response.data.items;
          this.pagination = response.data.pagination;
          if (this.pagination.totalPages < this.pagination.currentPage) {
            this.page = this.pagination.totalPages;
            this.getUsers();
          } else {
            this.loading = false;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
  },
};
</script>
